import { useEffect, useMemo, useState } from 'react';
import { FileText, Search, Trash2 } from '@/components/icons';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { Button } from '@/components/ui/Button';
import { FilterBar, FilterField } from '@/components/ui/FilterBar';
import { PeriodPicker, REPORT_UNITS, usePeriod } from '@/components/ui/PeriodPicker';
import { Table } from '@/components/ui/Table';
import { Pagination } from '@/components/ui/Pagination';
import { StatusBadge } from '@/components/ui/Badge';
import { SlaBadge } from '@/components/ui/SlaBadge';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { useToast } from '@/components/ui/useToast';
import { useConfirm } from '@/components/ui/useConfirm';
import { usePrompt } from '@/components/ui/usePrompt';
import {
  cancelPr,
  deletePr,
  listPrs,
  type Pr,
} from '@/api/pr';
import { apiError } from '@/lib/apiError';
import { formatDate, formatINRSymbol } from '@/lib/format';
import { useDocViewer } from '@/components/ui/useDocViewer';
import { PrFormModal } from './PrFormModal';

export function PrPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const confirmDlg = useConfirm();
  const ask = usePrompt();
  const docViewer = useDocViewer();
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  // PR date window — All / Date (from–to) / Month / Quarter / Year.
  const period = usePeriod();
  const { range } = period;
  useEffect(() => setPage(1), [range]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Pr | null>(null);

  const filter = useMemo(
    () => ({
      status: status || undefined,
      search: search || undefined,
      from: range?.from,
      to: range?.to,
      page,
      pageSize,
    }),
    [status, search, range, page, pageSize],
  );

  const query = useQuery({
    queryKey: ['pr', filter],
    queryFn: () => listPrs(filter),
    placeholderData: keepPreviousData,
  });

  function invalidate() {
    qc.invalidateQueries({ queryKey: ['pr'] });
  }

  const delMutation = useMutation({
    mutationFn: deletePr,
    onSuccess: () => {
      invalidate();
      toast.push('PR deleted', 'info');
    },
    onError: (e: unknown) =>
      toast.push(apiError(e, 'Delete failed'), 'error'),
  });

  const cancelMutation = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) => cancelPr(id, reason),
    onSuccess: () => {
      invalidate();
      toast.push('PR cancelled', 'info');
    },
    onError: (e: unknown) =>
      toast.push(apiError(e, 'Cancel failed'), 'error'),
  });

  function openAdd() {
    setEditing(null);
    setModalOpen(true);
  }
  function openEdit(p: Pr) {
    setEditing(p);
    setModalOpen(true);
  }
  async function onDelete(p: Pr) {
    if (!await confirmDlg(`Delete ${p.prNo}? This cannot be undone.`)) return;
    delMutation.mutate(p.id);
  }
  async function onCancel(p: Pr) {
    const reason = await ask({ title: `Cancel ${p.prNo}?`, label: 'Reason', multiline: true, tone: 'danger', confirmLabel: 'Cancel PR' });
    if (!reason?.trim()) return;
    cancelMutation.mutate({ id: p.id, reason });
  }

  const data = query.data?.data ?? [];
  const total = query.data?.total ?? 0;

  return (
    <div>
      <SectionHeader
        title="Purchase Requisition"      />

      <FilterBar>
        <FilterField label="Search">
          <div className="relative w-full">
            <Search
              size={11}
              className="pointer-events-none absolute left-1.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)] sm:left-2.5"
              strokeWidth={2.25}
            />
            <Input
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search"
              className="!pl-5 sm:!pl-8 sm:min-w-[240px]"
              aria-label="Search PRs"
            />
          </div>
        </FilterField>
        <FilterField label="Status">
          <Select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
            <option value="">All</option>
            <option>Draft</option>
            <option>Pending</option>
            <option>Approved</option>
            <option>RFQ Sent</option>
            <option>PO Generated</option>
            <option>Cancelled</option>
          </Select>
        </FilterField>
        <FilterField label="Period" className="col-span-3 sm:col-auto">
          <PeriodPicker {...period.picker} units={REPORT_UNITS} className="justify-start" />
        </FilterField>
        {/* Create chip lives inside the filter block on both viewports.
            Mobile: fills the empty grid slot. Desktop: right-pinned via ml-auto. */}
        <button
          type="button"
          onClick={openAdd}
          className="inline-flex h-7 items-center justify-center gap-1 rounded-lg bg-accent px-2 text-[11px] font-bold text-white shadow-sm transition hover:brightness-110 sm:ml-auto sm:h-8 sm:self-end sm:px-3 sm:text-xs"
        >
          ＋ New PR
        </button>
      </FilterBar>

      <Table<Pr>
        storageKey="pr"
        title="Purchase Requisitions"
        count={`Showing ${data.length} of ${total}`}
        rows={data}
        rowKey={(r) => r.id}
        loading={query.isFetching && data.length === 0}
        columns={[
          { key: 'prNo', header: 'PR #', width: '130px', cell: (r) => <span className="font-extrabold text-accent">{r.prNo}</span> },
          { key: 'date', header: 'Date', width: '110px', cell: (r) => formatDate(r.prDate) },
          { key: 'subject', header: 'Subject', width: '200px', cell: (r) => <span className="block max-w-[200px] truncate" title={r.subject ?? undefined}>{r.subject || '—'}</span> },
          { key: 'mrn', header: 'MRN Ref', width: '120px', cell: (r) => r.mrn?.mrnNo ? <span className="text-[11px] font-bold text-[#a78bfa]">{r.mrn.mrnNo}</span> : '—' },
          { key: 'dept', header: 'Dept', cell: (r) => r.department },
          { key: 'items', header: 'Items', width: '70px', cell: (r) => r.items.length },
          { key: 'value', header: 'Est. Value', width: '110px', align: 'right', cell: (r) => formatINRSymbol(r.totalValue) },
          { key: 'authority', header: 'Authority', width: '100px', cell: (r) => r.authority },
          { key: 'status', header: 'Status', width: '100px', cell: (r) => <StatusBadge status={r.status} /> },
          { key: 'sla', header: 'SLA', width: '60px', cell: (r) => <SlaBadge sla={r.sla ?? null} /> },
          {
            key: 'actions',
            header: '',
            width: '200px',
            cell: (r) => (
              <div className="flex items-center gap-1">
                <Button variant="glass" size="sm" onClick={() => openEdit(r)}>View</Button>
                <Button variant="glass" size="sm" onClick={() => docViewer.open('pr', r.id, r.prNo)} title="Open A4 document"><FileText size={14} /></Button>
                {(r.status === 'Pending' || r.status === 'Draft') && (
                  <span className="rounded-md bg-warning/10 px-2 py-0.5 text-[11px] font-bold text-warning" title="Awaiting approval on the PR Approval page">Awaiting Approval</span>
                )}
                {r.status === 'Draft' && (
                  <Button variant="glass" size="sm" onClick={() => onDelete(r)} title="Delete"><Trash2 size={14} /></Button>
                )}
                {r.status !== 'Cancelled' && r.status !== 'PO Generated' && r.status !== 'Draft' && (
                  <Button variant="danger" size="sm" onClick={() => onCancel(r)}>Cancel</Button>
                )}
              </div>
            ),
          },
        ]}
        footer={
          <Pagination
            page={page}
            pageSize={pageSize}
            total={total}
            setPage={setPage}
            setPageSize={setPageSize}
          />
        }
      />

      <PrFormModal open={modalOpen} editing={editing} onClose={() => setModalOpen(false)} />
    </div>
  );
}
